/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { ActionKernelType } from 'client-code/actions';

import { MAX_STORAGE_LIMIT } from '../../../config';
import { DeviceModel } from '../../../core/device';
import { InteractiveComponents } from '../../../core/enums';
import { DroppedServiceRequestError } from '../../../core/error';
import { NdvrInteractor, ServicesClientImpl } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { SharedPrefConstants, SharedPreferences } from '../../../core/storage';
import { TOverlayOptions, TRecordingOverlayIds, TRecordingValues } from '../../../core/types';
import { UIOrchestrator, UIOrchestratorState } from '../../../core/uiorchestrator';
import { WhisperTypes } from '../../../views/Whisper/WhisperTypes';
import {
    hideOverlayDialog,
    onKeyLeftOverlayDialog,
    onKeyRightOverlayDialog,
    showOverlayDialog,
} from '../../overlaydialog/overlaydialog.reducers';
import { IOverlayDialog } from '../../overlaydialog/overlaydialogprops';
import { setScreenTaskController } from '../../screens/screens.reducers';
import { showWhisper } from '../../whisper/whisper.reducer';
import { setOverlayHighlightVerticalPositionAction } from '../overlayhighlight/overlayhighlight.actions';
import { OverlayTypes } from '../OverlayTypes';
import { showStorageLimitOverlay } from '../storagelimitoverlay/storagelimitoverlay.reducers';
import { RecordingOptionsGenerator } from './RecordingOptionsGenerator';
import { RecordingOverlayOptionType } from './RecordingOverlayOptionType';

const GLOBAL_MOUSE_TRAP = 'HideRecordingOverlay';

export const clearScheduleRecordingSharedPreference = () => {
    const recordingSharedPreferenceEditor = new SharedPreferences(
        SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
    ).edit();
    recordingSharedPreferenceEditor.clear();
    recordingSharedPreferenceEditor.commit();
};

export const showRecordingOverlay =
    (recordingType: string, recordingOverlayIds: TRecordingOverlayIds, actionKernelType: string) =>
    async (dispatch: Function) => {
        const services = new ServicesClientImpl();
        UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.FETCHING_DATA);
        try {
            const recordingSharedPreferenceEditor = new SharedPreferences(
                SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
            ).edit();
            recordingSharedPreferenceEditor.putJson(
                SharedPrefConstants.RECORDING_GUIDECELL_INFO,
                recordingOverlayIds as unknown as JSON,
            );
            recordingSharedPreferenceEditor.commit();
            const recordingDisplayOptionsService = services.getRecordingOfferIdScheduleRequestModel(
                recordingOverlayIds.contentId,
                recordingOverlayIds.offerId,
            );
            const recordingOptionsService = services.getRecordingOfferIdOptionsRequestModel(
                recordingOverlayIds.contentId,
                recordingOverlayIds.offerId,
            );
            const recordingDisplayOptions = await recordingDisplayOptionsService.send();
            const recordingOptions = await recordingOptionsService.send();

            const recordingOptionsGenerator = new RecordingOptionsGenerator(
                recordingOptions,
                recordingDisplayOptions,
                recordingType,
                recordingOverlayIds,
                actionKernelType,
            );
            const overlayOptions = recordingOptionsGenerator.getOverlayOptions();
            const overlayProps: IOverlayDialog = {
                enableCaret: true,
                hasDimmer: true,
                hasSeperator: false,
                mouseTrapValue: GLOBAL_MOUSE_TRAP,
                options: overlayOptions,
                overlayType: OverlayTypes.RECORDING,
                title: Locale.getString(LocaleKeys.RECORDING_OVERLAY_OPTION_TITLE),
            };
            UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.DATA_LOADED);
            dispatch(setScreenTaskController(ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS));
            dispatch(showOverlayDialog(overlayProps));
        } catch (error) {
            Log.e(GLOBAL_MOUSE_TRAP, 'error while showRecordingOverlay: ', error);
            UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.DATA_LOADED);
            clearScheduleRecordingSharedPreference();
            if (!(error instanceof DroppedServiceRequestError)) {
                const whisperMessage = Locale.getString(LocaleKeys.FAILURE_WHISPER_MESSAGE);
                dispatch(showWhisper({ message: whisperMessage, iconType: WhisperTypes.NO_ICON }));
            }
        }
    };

export const onKeyBackRecordingOverlay = () => (dispatch: Function) => {
    dispatch(hideRecordingOverlay());
};

export const onKeyLeftRecordingOverlay = () => (dispatch: Function) => {
    dispatch(onKeyLeftOverlayDialog());
    dispatch(setSelectedHorizontalOptions());
};

export const onKeyRightRecordingOverlay = () => (dispatch: Function) => {
    dispatch(onKeyRightOverlayDialog());
    dispatch(setSelectedHorizontalOptions());
};

const setSelectedHorizontalOptions = () => (_: Function, getState: Function) => {
    const { overlayHighlight, overlayDialog } = getState();
    const {
        verticalPosition: overlayHighlightVerticalPosition,
        horizontalPosition: overlayHighlightHorizontalPosition,
    } = overlayHighlight;
    const { uiProperties } = overlayDialog;
    const { options } = uiProperties;
    const selectedOption = options[overlayHighlightVerticalPosition];
    RecordingOptionsGenerator.updateRecordingOptionsOverlaySelectedIndex(
        selectedOption,
        overlayHighlightHorizontalPosition,
        overlayHighlightVerticalPosition,
    );
};

export const onMouseClickRecordingOverlay = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;

    if (value === GLOBAL_MOUSE_TRAP) {
        dispatch(hideRecordingOverlay());
        return;
    }
    if (value === InteractiveComponents.EAST) dispatch(onKeyRightRecordingOverlay());
    else if (value === InteractiveComponents.WEST) dispatch(onKeyLeftRecordingOverlay());
    else dispatch(onKeyOkRecordingOverlay());
};

const hideRecordingOverlay = () => (dispatch: Function) => {
    dispatch(hideOverlayDialog());
    RecordingOptionsGenerator.reset();
};

export const showCancelRecordingOverlay = (recordType: string) => async (dispatch: Function) => {
    const overlayOptions: Array<TOverlayOptions> = [
        {
            hrTop: true,
            text: Locale.getString(LocaleKeys.CANCEL_RECORDING_OVERLAY_OPTION_YES),
        },
        {
            text: Locale.getString(LocaleKeys.CANCEL_RECORDING_OVERLAY_OPTION_NO),
        },
    ];

    const overlayProps: IOverlayDialog = {
        description: `${Locale.getString(LocaleKeys.CANCEL_RECORDING_OVERLAY_DESCRIPTION)}`,
        enableCaret: true,
        hasDimmer: true,
        hasSeperator: false,
        mouseTrapValue: GLOBAL_MOUSE_TRAP,
        options: overlayOptions,
        overlayType: OverlayTypes.STOP_RECORDING,
        title: Locale.getString(LocaleKeys.CANCEL_RECORDING_OVERLAY_TITLE),
    };
    dispatch(hideRecordingOverlay());
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS));
    dispatch(showOverlayDialog(overlayProps));
};

export const showStopAndDeleteRecordingOverlay =
    (recordType: string) => async (dispatch: Function) => {
        const overlayOptions: Array<TOverlayOptions> = [
            {
                hrTop: true,
                text: Locale.getString(
                    LocaleKeys.RECORDING_OVERLAY_OPTION_STOP_RECORDING_AND_DELETE,
                ),
            },
            {
                text: Locale.getString(LocaleKeys.OVERLAY_OPTION_CANCEL_TEXT),
            },
        ];

        const recordingTypes = new Set([
            'SONG',
            'EVENT',
            'WEB_VIDEO',
            'RACE',
            'RENT',
            'MATCH',
            'MEET',
            'SEREIS',
            'EPISODE',
            'MOVIE',
            'SHOW',
            'GAME',
        ]);
        let recordingTypeText: string;
        if (recordingTypes.has(recordType.toUpperCase())) {
            const recordingTypeKey = `RECORDING_OVERLAY_OPTION_THIS_${recordType.toUpperCase()}`;
            recordingTypeText = Locale.getString(LocaleKeys[recordingTypeKey]);
        } else {
            recordingTypeText = '';
        }
        const recordText =
            recordingTypeText.length > 0
                ? `${Locale.getString(LocaleKeys.STOP_RECORDING_OVERLAY_DESCRIPTION)} ${recordingTypeText}`
                : `${Locale.getString(LocaleKeys.STOP_RECORDING_OVERLAY_DESCRIPTION_THIS)} ${recordType.toLowerCase()}`;
        const overlayProps: IOverlayDialog = {
            description: `${recordText}?`,
            enableCaret: true,
            hasDimmer: true,
            hasSeperator: false,
            mouseTrapValue: GLOBAL_MOUSE_TRAP,
            options: overlayOptions,
            overlayType: OverlayTypes.STOP_RECORDING,
            title: Locale.getString(LocaleKeys.RECORDING_OVERLAY_OPTION_STOP_RECORDING),
        };
        dispatch(hideRecordingOverlay());
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS));
        dispatch(showOverlayDialog(overlayProps));
    };

export const onKeyOkStopOrCancelRecording =
    (recordingOverlayIds?: TRecordingOverlayIds) =>
    async (dispatch: Function, getState: Function) => {
        try {
            const services = new NdvrInteractor();
            const { guideRows } = getState().gridGuide;
            const { horizontalPosition, verticalPosition: guideVerticalPosition } =
                getState().guideHighlight;
            const mGuideCells = guideRows[guideVerticalPosition]?.guideCells;
            const guideCellData = mGuideCells && mGuideCells[horizontalPosition - 1];
            const { verticalPosition: overlayHighlightVerticalPosition } =
                getState().overlayHighlight;
            dispatch(hideOverlayDialog());
            const recordingOfferId = DeviceModel.getSharedPreferences(
                SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
            ).getString(SharedPrefConstants.RECORDING_OFFER_ID);
            const recordingContentId = DeviceModel.getSharedPreferences(
                SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
            ).getString(SharedPrefConstants.RECORDING_CONTENT_ID);
            if (!overlayHighlightVerticalPosition) {
                await services
                    .getRequestModelToCancelRecordingOfferIdSchedule(
                        recordingOverlayIds?.contentId ??
                            recordingContentId ??
                            guideCellData.contentId,
                        recordingOverlayIds?.offerId ?? recordingOfferId ?? guideCellData.offerId,
                    )
                    .send();
                UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.RECORDING_DELETED);
            }
        } catch (error) {
            const whisperMessage = Locale.getString(LocaleKeys.FAILURE_WHISPER_MESSAGE);
            dispatch(showWhisper({ message: whisperMessage, iconType: WhisperTypes.NO_ICON }));
        }
    };

const scheduleRecording = () => async (dispatch: Function) => {
    try {
        const guideCellData = new SharedPreferences(
            SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
        ).getJson(SharedPrefConstants.RECORDING_GUIDECELL_INFO) as any;
        const tRecordingValues = new SharedPreferences(
            SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
        ).getJson(SharedPrefConstants.RECORDING_USER_SELECTED_OPTIONS) as any;
        const recordingType = new SharedPreferences(
            SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
        ).getString(SharedPrefConstants.RECORDING_ACTION_KERNEL_TYPE);
        const services = new ServicesClientImpl();
        await services
            .getRequestModelToCreateRecordingOfferIdSchedule(
                guideCellData.contentId,
                guideCellData.offerId,
                tRecordingValues,
            )
            .send();
        const actionKernelType = RecordingOptionsGenerator.getActionKernelType();
        const whisperIcon = actionKernelType.includes('modify')
            ? WhisperTypes.NO_ICON
            : WhisperTypes.RECORDING_SUCCESS;
        const whisperMessage = actionKernelType.includes('modify')
            ? Locale.getString(LocaleKeys.MODIFY_RECORDING_WHISPER_MESSAGE)
            : Locale.getString(LocaleKeys[`RECORDING_WHISPER_MESSAGE_FOR_${recordingType}_TYPE`]);
        if (actionKernelType === ActionKernelType.ModifyRecordingActionKernel) {
            UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.RECORDING_MODIFIED);
        } else {
            UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.RECORDING_CREATED);
        }
        dispatch(showWhisper({ message: whisperMessage, iconType: whisperIcon }));
    } catch (error) {
        const whisperMessage = Locale.getString(LocaleKeys.FAILURE_WHISPER_MESSAGE);
        dispatch(showWhisper({ message: whisperMessage, iconType: WhisperTypes.NO_ICON }));
    } finally {
        dispatch(hideRecordingOverlay());
    }
};

const checkStorageAndPerformRecordingAction = (options, guideCellData) => (dispatch: Function) => {
    const recordingSharedPreferenceEditor = new SharedPreferences(
        SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
    ).edit();
    recordingSharedPreferenceEditor.putJson(
        SharedPrefConstants.RECORDING_GUIDECELL_INFO,
        guideCellData as unknown as JSON,
    );
    recordingSharedPreferenceEditor.putJson(
        SharedPrefConstants.RECORDING_USER_SELECTED_OPTIONS,
        options as unknown as JSON,
    );
    recordingSharedPreferenceEditor.commit();
    const diskSpacePercentageUsed = new SharedPreferences(
        SharedPrefConstants.DISK_SPACE_PREF_KEY,
    ).getNumber(SharedPrefConstants.DISK_SPACE_PERCENTAGE_USED);
    if (diskSpacePercentageUsed > MAX_STORAGE_LIMIT) {
        dispatch(showStorageLimitOverlay(scheduleRecording));
        return;
    }
    dispatch(scheduleRecording());
};

export const onKeyOkRecordingOverlay = () => async (dispatch: Function, getState: Function) => {
    const { overlayHighlight, overlayDialog } = getState();
    const { verticalPosition: overlayHighlightVerticalPosition } = overlayHighlight;
    const { uiProperties } = overlayDialog;
    const { options } = uiProperties;
    const selectedOption = options[overlayHighlightVerticalPosition];

    switch (selectedOption.value.actionType) {
        case RecordingOverlayOptionType.CANCEL:
        case RecordingOverlayOptionType.NO_CHANGE_IN_RECORDING_OPTION:
            dispatch(hideRecordingOverlay());
            break;

        case RecordingOverlayOptionType.CANCEL_RECORDING:
            dispatch(showCancelRecordingOverlay(selectedOption.value.recordType));
            break;

        case RecordingOverlayOptionType.STOP_AND_DELETE_RECORDING:
            dispatch(showStopAndDeleteRecordingOverlay(selectedOption.value.recordType));
            break;

        case RecordingOverlayOptionType.KEEP_UNTIL:
        case RecordingOverlayOptionType.START_RECORDING:
        case RecordingOverlayOptionType.STOP_RECORDING:
        case RecordingOverlayOptionType.TIME_AND_CHANNEL:
            dispatch(setOverlayHighlightVerticalPositionAction(0));
            break;

        case RecordingOverlayOptionType.SCHEDULE_RECORDING: {
            const tRecordingValues: TRecordingValues =
                RecordingOptionsGenerator.getRecordingValuesForSelectedOption(options);
            const guideCellData = RecordingOptionsGenerator.getRecordingOptions('guideCellData');
            const recordingOfferId = DeviceModel.getSharedPreferences(
                SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
            ).getString(SharedPrefConstants.RECORDING_OFFER_ID);
            const recordingContentId = DeviceModel.getSharedPreferences(
                SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
            ).getString(SharedPrefConstants.RECORDING_CONTENT_ID);
            const contentParams =
                recordingContentId && recordingOfferId
                    ? { contentId: recordingContentId, offerId: recordingOfferId }
                    : null;
            dispatch(
                checkStorageAndPerformRecordingAction(
                    tRecordingValues,
                    contentParams ?? guideCellData,
                ),
            );
            break;
        }

        default:
    }
};

export const onMouseClickStopOrCancelRecording = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;

    if (value === GLOBAL_MOUSE_TRAP) {
        dispatch(hideOverlayDialog());
        return;
    }
    dispatch(onKeyOkStopOrCancelRecording());
};
